import type { UserPreferences, LayerPrefRecord } from '@my-fullstack-app/itinerary-types';
import { useItineraryStore } from '../store/itinerary-store';
import { useConsentStore } from '../store/consent-store';
import { getAccessToken } from './auth-store';
import { consumePendingPrefsMigration } from './pending-prefs-migration';
import { defaultLayerPrefs, type LayerId } from './layer-registry';
import { defaultReviewLayerPrefs, type ReviewLayerId } from './review-layer-registry';
import { DEFAULT_HAPTICS_ENABLED } from '../store/slices/view-slice';
import { Debouncer, Throttler } from './sync-primitives';

export interface UserPrefsApi {
  get: () => Promise<UserPreferences>;
  update: (prefs: Partial<UserPreferences>) => Promise<UserPreferences>;
}

/** Anonymous→authenticated migration for one pref record. For each
 *  id in `anon.explicit` that the server's explicit set lacks AND the
 *  registry still recognises, promote: add to local explicit, copy
 *  anon's value into the local values map. Returns the new values +
 *  explicit, or `null` if nothing actually changed.
 *
 *  The "registry still recognises" filter uses `Object.hasOwn` to
 *  reject prototype-named keys from a malformed anon blob. */
function mergeAnonExplicit(
  anon: LayerPrefRecord,
  server: LayerPrefRecord | undefined,
  currentValues: Record<string, boolean>,
  currentExplicit: readonly string[],
  defaults: Record<string, boolean>,
): { values: Record<string, boolean>; explicit: string[] } | null {
  const serverExplicit = new Set(server?.explicit ?? []);
  const mergedValues: Record<string, boolean> = { ...currentValues };
  const mergedExplicit = new Set(currentExplicit);
  let changed = false;
  for (const id of anon.explicit) {
    if (!Object.hasOwn(defaults, id)) continue;
    if (serverExplicit.has(id)) continue;
    const v = anon.values[id];
    if (typeof v !== 'boolean') continue;
    mergedValues[id] = v;
    mergedExplicit.add(id);
    changed = true;
  }
  if (!changed) return null;
  return { values: mergedValues, explicit: [...mergedExplicit] };
}

/** Compose the in-memory values map from a server-side
 *  `LayerPrefRecord` overlaid on the current registry defaults.
 *  Server's explicit set drives which ids get the server's value; the
 *  rest get the registry default. Filters out ids the server has but
 *  the local registry doesn't know about (a removed layer) so they
 *  don't sneak into the local map. Returns the values map plus the
 *  filtered explicit array — caller MUST set both.
 *
 *  Defensive against a legacy record shape: per ADR-0082 the cutover
 *  shipped without a Dynamo migration, so pre-cutover writes are still
 *  on disk as `Record<string, boolean>`. The runtime payload type
 *  (`LayerPrefRecord`) won't actually hold here for those records.
 *  Treat any non-conforming input as "no overrides" — matches the
 *  ADR's "existing records effectively dropped" semantic. Without
 *  this guard the `for (const id of record.explicit)` below throws
 *  on undefined and the entire hydrate try/catch swallows the error,
 *  leaving the user invisible to their own server prefs until they
 *  toggle one layer manually. */
function applyServerLayerRecord(
  record: LayerPrefRecord,
  defaults: Record<string, boolean>,
): { values: Record<string, boolean>; explicit: string[] } {
  const values: Record<string, boolean> = { ...defaults };
  const explicit: string[] = [];
  if (
    !Array.isArray(record.explicit) ||
    record.values === null ||
    typeof record.values !== 'object'
  ) {
    return { values, explicit };
  }
  for (const id of record.explicit) {
    if (!Object.hasOwn(defaults, id)) continue;
    const v = record.values[id];
    if (typeof v !== 'boolean') continue;
    values[id] = v;
    explicit.push(id);
  }
  return { values, explicit };
}

/** Build the wire-format `LayerPrefRecord` from an explicit-set + a
 *  values map. ALWAYS returns a record, even when the explicit set is
 *  empty — `snapshot()` always ships the field so a `resetLayers()`
 *  action (which sets explicit=[]) reaches the server and overwrites
 *  the prior explicit set via spread-merge. If we omitted the field
 *  on empty-explicit, the server's prior `{explicit: ['photos'], …}`
 *  would survive and the next hydrate would silently undo the reset. */
function buildExplicitPayload(
  explicit: readonly string[],
  values: Partial<Record<string, boolean>>,
): LayerPrefRecord {
  const out: Record<string, boolean> = {};
  for (const id of explicit) {
    const v = values[id];
    if (typeof v === 'boolean') out[id] = v;
  }
  // Defensive: explicit ids whose value is missing or non-boolean (a
  // legacy bug from before this PR landed) get dropped from `values`
  // but still take a slot in `explicit`. The server would round-trip
  // the dangling id back and the next applyServer would default-fill
  // it. Acceptable — the user can re-toggle to recover the intended
  // value.
  return { explicit: [...explicit], values: out };
}

/**
 * Lightweight sync layer for user-level preferences. Distinct from the
 * per-trip SyncManager — preferences are a single record per user and
 * tiny enough to ship the whole thing on each flush, so we don't need
 * the field-level dirty tracking the trip sync uses.
 *
 * Flow:
 *   - `hydrate()`     pulls the server record, merges with local. Local
 *                     edits (anything that flipped `dirtyUserPrefs` since
 *                     the last successful flush) win on conflict.
 *   - `scheduleFlush` debounces by ~1.5s and pushes whichever local prefs
 *                     are currently in the store. The whole record goes
 *                     out on each push; server-side merge handles partial
 *                     updates from older clients.
 *   - `flushImmediate` for app-background / page-hide.
 */
export class UserPrefsSync {
  // Pull at most once per ~10s on focus events; auth change forces a refresh.
  private static readonly HYDRATE_THROTTLE_MS = 10_000;
  private static readonly FLUSH_DEBOUNCE_MS = 1_500;
  private static instance: UserPrefsSync | undefined;
  private api: UserPrefsApi | null = null;
  private flushDebouncer = new Debouncer(UserPrefsSync.FLUSH_DEBOUNCE_MS, () =>
    this.flushImmediate(),
  );
  private hydrateThrottler = new Throttler(UserPrefsSync.HYDRATE_THROTTLE_MS);
  private inFlight = false;

  private constructor() { /* singleton */ }

  static getInstance(): UserPrefsSync {
    UserPrefsSync.instance ??= new UserPrefsSync();
    return UserPrefsSync.instance;
  }

  /** Test helper. Reset all instance state so each `it()` starts fresh. */
  static _reset(): void {
    UserPrefsSync.instance?.flushDebouncer.cancel();
    UserPrefsSync.instance = new UserPrefsSync();
  }

  init(api: UserPrefsApi): void {
    this.api = api;
  }

  /**
   * Build the wire payload from the current store state. Centralised so
   * both flush and tests grab the same shape.
   *
   * Per ADR-0082, `layerPrefs` / `reviewLayerPrefs` ship as
   * `{ explicit, values }` containing ONLY the user's directly-toggled
   * ids — not the full registry map. The field is ALWAYS included
   * (even when explicit is empty) so a `resetLayers()` flush can
   * actually clear the server's prior explicit set; omitting the
   * field would let the server's spread-merge preserve the old
   * record and the next hydrate would silently undo the reset.
   * The wire cost is ~25 bytes per empty record — negligible.
   */
  private snapshot(): Partial<UserPreferences> {
    const s = useItineraryStore.getState();
    const consent = useConsentStore.getState().consent;
    const payload: Partial<UserPreferences> = {
      favoriteTripIds: s.favoriteTripIds,
      hapticsEnabled: s.hapticsEnabled,
      tutorialDisabled: s.tutorialDisabled,
      layerPrefs: buildExplicitPayload(s.layerPrefsExplicit, s.layerPrefs),
      reviewLayerPrefs: buildExplicitPayload(
        s.reviewLayerPrefsExplicit,
        s.reviewLayerPrefs,
      ),
    };
    // Only ship `tutorialSeenAt` once it exists — absent means "never
    // finished the tour", which the server should not overwrite with a
    // stale null on a partial update from a client that hasn't seen it.
    if (s.tutorialSeenAt) payload.tutorialSeenAt = s.tutorialSeenAt;
    // Only include `consent` when the user has actually decided — the
    // server treats absent and `null` differently (absent = no
    // decision yet, can be migrated from anonymous; null = explicit
    // clear, which we don't currently expose to users).
    if (consent) payload.consent = consent;
    return payload;
  }

  /**
   * Apply a server-resolved record to the local store. Server values
   * win unless the field has been edited locally since the last hydrate
   * (i.e. dirtyUserPrefs is true) — that protects in-flight typing.
   *
   * EXCEPTION (ADR-0065): consent is server-wins even when dirty.
   * The dirty bit protects mid-keystroke edits on free-text fields
   * (currency, nationality); consent is set atomically through the
   * sheet, and the ADR's "server wins on conflict" rule supersedes
   * the typing-protection escape valve. Without this carve-out, an
   * anonymous local decision would beat the server's prior decision
   * from another device on the very next sign-in.
   *
   * Returns `true` if the dirty-skip path was taken (server values
   * were NOT applied) — `hydrate` uses this to also skip the
   * pref-migration arm, since blindly merging the anon stash on top
   * of an in-flight user edit would overwrite the edit.
   */
  private applyServer(server: UserPreferences): boolean {
    const s = useItineraryStore.getState();

    // Consent first — applies regardless of dirty. Absent means "no
    // decision on the server yet" — leave the local AsyncStorage
    // decision (if any) in place so `hydrate`'s migration arm below
    // can push it on the next flush.
    if (server.consent) {
      useConsentStore.getState().hydrateFromServer(server.consent);
    }

    if (s.dirtyUserPrefs) {
      // Local has edits we haven't flushed yet for the dirty-gated
      // fields — don't clobber them, but do refresh the hydration
      // anchor to reflect this view.
      if (server.updatedAt) s.setUserPrefsHydratedAt(server.updatedAt);
      return true;
    }
    const next: Partial<{
      favoriteTripIds: string[];
      layerPrefs: Record<string, boolean>;
      layerPrefsExplicit: LayerId[];
      reviewLayerPrefs: Record<string, boolean>;
      reviewLayerPrefsExplicit: ReviewLayerId[];
      hapticsEnabled: boolean;
      tutorialDisabled: boolean;
      tutorialSeenAt: string | null;
    }> = {};
    if (server.favoriteTripIds) next.favoriteTripIds = server.favoriteTripIds;
    if (server.layerPrefs) {
      const applied = applyServerLayerRecord(
        server.layerPrefs,
        defaultLayerPrefs(),
      );
      next.layerPrefs = applied.values;
      next.layerPrefsExplicit = applied.explicit as LayerId[];
    }
    if (server.reviewLayerPrefs) {
      const applied = applyServerLayerRecord(
        server.reviewLayerPrefs,
        defaultReviewLayerPrefs(),
      );
      next.reviewLayerPrefs = applied.values;
      next.reviewLayerPrefsExplicit = applied.explicit as ReviewLayerId[];
    }
    if (typeof server.hapticsEnabled === 'boolean') next.hapticsEnabled = server.hapticsEnabled;
    if (typeof server.tutorialDisabled === 'boolean') next.tutorialDisabled = server.tutorialDisabled;
    if (typeof server.tutorialSeenAt === 'string') next.tutorialSeenAt = server.tutorialSeenAt;
    useItineraryStore.setState(next as Record<string, unknown>);
    if (server.updatedAt) s.setUserPrefsHydratedAt(server.updatedAt);
    return false;
  }

  /** Pull the server record and merge into the store. No-ops without API. */
  async hydrate(opts: { force?: boolean | undefined} = {}): Promise<void> {
    if (!this.api) return;
    if (this.hydrateThrottler.shouldSkip(opts.force)) return;
    try {
      const server = await this.api.get();
      const dirtySkipped = this.applyServer(server);
      // Anonymous→authenticated pref migration: claimAndSwitchToUser
      // stashed the just-vacated anonymous scope's pref record. Drain
      // the stash unconditionally (so the next sign-in starts clean)
      // but only act on it if applyServer actually ran — when
      // applyServer dirty-skipped, blindly merging the stash on top of
      // an in-flight user edit would overwrite that edit.
      //
      // Per ADR-0082, explicit-ness is the gate (not value-vs-default).
      // For each id in anon's explicit set: if the server's explicit
      // set lacks it AND the registry still has it, promote — add to
      // local explicit, copy anon's value into the local values map.
      const pending = consumePendingPrefsMigration();
      if (pending && !dirtySkipped) {
        const updates: {
          layerPrefs?: Record<string, boolean>;
          layerPrefsExplicit?: LayerId[];
          reviewLayerPrefs?: Record<string, boolean>;
          reviewLayerPrefsExplicit?: ReviewLayerId[];
          hapticsEnabled?: boolean;
        } = {};
        const current = useItineraryStore.getState();
        let anyApplied = false;

        if (pending.layerPrefs) {
          const out = mergeAnonExplicit(
            pending.layerPrefs,
            server.layerPrefs,
            current.layerPrefs,
            current.layerPrefsExplicit,
            defaultLayerPrefs(),
          );
          if (out) {
            updates.layerPrefs = out.values;
            updates.layerPrefsExplicit = out.explicit as LayerId[];
            anyApplied = true;
          }
        }

        if (pending.reviewLayerPrefs) {
          const out = mergeAnonExplicit(
            pending.reviewLayerPrefs,
            server.reviewLayerPrefs,
            current.reviewLayerPrefs,
            current.reviewLayerPrefsExplicit,
            defaultReviewLayerPrefs(),
          );
          if (out) {
            updates.reviewLayerPrefs = out.values;
            updates.reviewLayerPrefsExplicit = out.explicit as ReviewLayerId[];
            anyApplied = true;
          }
        }

        if (
          typeof pending.hapticsEnabled === 'boolean' &&
          typeof server.hapticsEnabled !== 'boolean' &&
          pending.hapticsEnabled !== DEFAULT_HAPTICS_ENABLED
        ) {
          updates.hapticsEnabled = pending.hapticsEnabled;
          anyApplied = true;
        }

        if (anyApplied) {
          useItineraryStore.setState(updates as Record<string, unknown>);
          useItineraryStore.getState().markUserPrefsDirty();
        }
      }
      // Anonymous→authenticated consent migration (ADR-0065): if the
      // server has no decision recorded but the local store does
      // (set while the user was anonymous), mark prefs dirty so the
      // next flush pushes the local decision up. Server-wins still
      // applies in `applyServer` when the server DOES have a value.
      // Intentionally runs even on the dirty-skip path: markDirty on
      // an already-dirty bit is a no-op, and the user's pending
      // non-consent edit will carry the consent up alongside it when
      // it flushes (snapshot reads consent from useConsentStore).
      if (server.consent === undefined && useConsentStore.getState().consent !== null) {
        useItineraryStore.getState().markUserPrefsDirty();
      }
      this.hydrateThrottler.markFired();
    } catch (e) {
      console.warn('[user-prefs] hydrate failed', e);
    }
  }

  /**
   * Debounced flush — call after every action that mutates a synced
   * pref. Coalesces bursts (e.g. user toggling several layers in a row)
   * into a single PUT.
   */
  scheduleFlush(): void {
    this.flushDebouncer.schedule();
  }

  /** Flush without waiting for the debounce — used on app background / page hide. */
  async flushImmediate(): Promise<void> {
    if (!this.api) return;
    this.flushDebouncer.cancel();
    // No token → the server endpoint is protectedProcedure and would
    // 401. This catches three callers that previously generated noise:
    //   1. Anonymous users with `dirtyUserPrefs: true` (e.g. consent set
    //      while anonymous) firing on every AppState background event.
    //   2. The post-clearAuth tail of switchToAnonymous in the
    //      auth-expired event handler — token is already null.
    //   3. The handleOnline / lifecycle handlers when not signed in.
    // The dirty bit stays set; SyncManagerInit's [isAuthenticated]
    // effect flushes once the user authenticates.
    if (!getAccessToken()) return;
    const s = useItineraryStore.getState();
    if (!s.dirtyUserPrefs) return;
    if (this.inFlight) return; // Another flush will pick up the latest state
    this.inFlight = true;
    try {
      const sent = this.snapshot();
      const server = await this.api.update(sent);
      // Latest state may have diverged while the request was in flight; we
      // only clear dirty when the post-flush snapshot is unchanged from
      // what we just sent. Simplest correct approach for tiny payloads:
      // re-read after the await.
      const after = useItineraryStore.getState();
      if (JSON.stringify(sent) === JSON.stringify(this.snapshot())) {
        after.clearUserPrefsDirty();
      }
      if (server.updatedAt) after.setUserPrefsHydratedAt(server.updatedAt);
    } catch (e) {
      console.warn('[user-prefs] flush failed', e);
    } finally {
      this.inFlight = false;
    }
  }
}
